import React, {PropsWithChildren} from 'react';
import classNames from 'classnames';
import {Colors} from '../../types/colorTypes';

export enum SectionSpacing {
  NORMAL = 'normal',
  LARGE = 'large',
  TOP_ONLY = 'top-only',
  BOTTOM_ONLY = 'bottom-only',
  TOP_ONLY_LARGE = 'top-only-large',
  BOTTOM_ONLY_LARGE = 'bottom-only-large',
  SMALL = 'small',
  MEDIUM = 'medium',
  TOP_NEGATIVE = 'top-negative'
}

export enum SectionMaxWidth {
  LARGE = 'large',
  SMALL = 'small',
  MEDIUM = 'medium',
  NORMAL = 'normal'
}

export enum SectionMargin {
  NONE = 'none',
  TOP_ONLY = 'top-only',
  BOTTOM_ONLY = 'bottom-only',
  BOTH = 'both'
}

export enum SectionRounded {
  NONE = 'none',
  TOP_ONLY = 'top-only',
  BOTTOM_ONLY = 'bottom-only',
  BOTH = 'both'
}

export type SectionProps = {
  bg?: Colors;
  spacing?: SectionSpacing;
  margin?: SectionMargin;
  maxWidth?: SectionMaxWidth;
  noPaddingX?: boolean;
  dataTestId?: string;
  roundedCorners?: SectionRounded;
  isBlogPost?: boolean;
};

export const Section: React.FC<PropsWithChildren<SectionProps>> = props => {
  const classes = classNames({
    'bg-grey-bright': props.bg === Colors.BRIGHTGREY,
    'bg-primary': props.bg === Colors.PRIMARY,
    'py-10 md:py-20': props.spacing !== SectionSpacing.LARGE && props.spacing !== SectionSpacing.MEDIUM,
    'py-16 md:py-32': props.spacing === SectionSpacing.LARGE,
    'pb-1 md:pb-1': props.spacing === SectionSpacing.TOP_ONLY,
    'pt-1 md:pt-1': props.spacing === SectionSpacing.BOTTOM_ONLY,
    'py-16 md:py-32 pb-1 md:pb-1': props.spacing === SectionSpacing.TOP_ONLY_LARGE,
    'py-16 md:py-32 pt-1 md:pt-1': props.spacing === SectionSpacing.BOTTOM_ONLY_LARGE,
    'pb-small md:pb-small pt-small md:pt-small': props.spacing === SectionSpacing.SMALL,
    'py-5 md:py-5': props.spacing === SectionSpacing.MEDIUM,
    // for blogPosts only
    '-mt-24 sm:-mt-32': props.spacing === SectionSpacing.TOP_NEGATIVE && props.margin === SectionMargin.NONE,
    'my-4': props.margin === SectionMargin.BOTH,
    'mt-4': props.margin === SectionMargin.TOP_ONLY,
    'mb-4': props.margin === SectionMargin.BOTTOM_ONLY,
    'rounded-t': props.roundedCorners === SectionRounded.TOP_ONLY,
    'rounded-b': props.roundedCorners === SectionRounded.BOTTOM_ONLY,
    rounded: props.roundedCorners === SectionRounded.BOTH
  });

  const classesInner = classNames({
    container: !props.isBlogPost,
    'max-w-screen-xl': props.maxWidth === SectionMaxWidth.LARGE,
    'max-w-screen-lg': props.maxWidth === SectionMaxWidth.NORMAL || props.maxWidth === undefined,
    'max-w-screen-md': props.maxWidth === SectionMaxWidth.MEDIUM,
    'max-w-screen-sm': props.maxWidth === SectionMaxWidth.SMALL,
    'p-0': props.noPaddingX,
    'pt-32 sm:pt-12': props.spacing === 'top-negative'
  });

  return (
    <section className={classes} data-testid={props.dataTestId}>
      <div className={classesInner}>{props.children}</div>
    </section>
  );
};
